import { flowRight } from 'lodash';
import React from 'react';
import { MutationFn } from 'react-apollo';
import I18n from 'i18n-js';

import Currency from '@reverbdotcom/commons/src/currency';
import { parseAmount, parseAmountCents, formatNumber } from '@reverbdotcom/commons/src/money';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroupWithCheckbox from '@reverbdotcom/commons/src/components/form_group_with_checkbox';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import { createAutoOfferMutation, deleteAutoOfferMutation } from '@reverbdotcom/commons/src/offers/offer_operations';

import Location from '../../lib/wrapped_location';
import {
  AutoOfferListingFragment,
  CoreAutoOffersCreateAutoOfferMutation,
  CoreAutoOffersDeleteAutoOfferMutation,
  core_apimessages_NegotiationType,
} from '../../schemas/rql';

interface IExternalProps {
  loading: boolean;
  listing: AutoOfferListingFragment.Fragment;
  closeModal: () => void;
}

interface IApolloProps {
  createAutoOffer: MutationFn<
  CoreAutoOffersCreateAutoOfferMutation.Mutation,
  CoreAutoOffersCreateAutoOfferMutation.Variables
  >;
  deleteAutoOffer: MutationFn<
  CoreAutoOffersDeleteAutoOfferMutation.Mutation,
  CoreAutoOffersDeleteAutoOfferMutation.Variables
  >;
}

export type IProps = IExternalProps & IApolloProps;

export interface IState {
  offerPrice: string;
  messageToBuyer: string;
  autoOfferIsActive: boolean;
  submitFailedMessage: string;
}

interface IPriceHelperProps {
  listing: AutoOfferListingFragment.Fragment;
  dispatch: React.Dispatch<any>;
}

const emptyFormState = {
  autoOfferIsActive: false,
  offerPrice: '',
  messageToBuyer: '',
  submitFailedMessage: '',
};

const MINIMUM_DISCOUNT_PERCENT = 10;
const PRICE_HELPER_BUTTON_OPTIONS = [10, 15, 20];

function formReducer(state, action): IState {
  switch (action.type) {
    case 'prefillFormState':
      return {
        ...state,
        autoOfferIsActive: true,
        offerPrice: formatNumber(action.value.listing.autoOffers.price.amount),
        messageToBuyer: action.value.listing.autoOffers.message,
      };
    case 'toggleOfferActive':
      return { ...state, autoOfferIsActive: !state.autoOfferIsActive };
    case 'setOfferPrice':
      return { ...state, offerPrice: action.value };
    case 'setOfferPriceFromPriceHelper':
      return {
        ...state,
        offerPrice: discountedPrice(action.value.amountCents, action.value.currency, action.value.percentDiscount),
      };
    case 'setMessageToBuyer':
      return { ...state, messageToBuyer: action.value };
    case 'setSubmitFailedMessage':
      return { ...state, submitFailedMessage: action.value };
    default:
      return { ...state };
  }
}

function discountedPrice(amountCents: number, currency: string, percentDiscount: number): string {
  const discountMultiplier = (100 - percentDiscount) / 100;
  const discountedPriceCents = Math.floor(amountCents * discountMultiplier);
  const parsed = parseAmountCents(discountedPriceCents, currency);

  return formatNumber(Number(parsed.amount));
}

function hasAutoOffer(listing: AutoOfferListingFragment.Fragment) {
  return listing && !!listing.autoOffers.id;
}

function hasDisabledExistingAutoOffer(formState: IState, listing: AutoOfferListingFragment.Fragment) {
  return !formState.autoOfferIsActive && hasAutoOffer(listing);
}

function NegotiationCountBox({ listing }) {
  const aggResults = listing.aggregationsBy || [];

  const autoOfferType = aggResults.find(
    ({ optionValue }) => `${optionValue}`.toUpperCase() === core_apimessages_NegotiationType.AUTO_PUSH_OFFER,
  );

  if (!autoOfferType || autoOfferType.count.value < 1) return null;

  return (
    <div className="scaling-mb-2">
      <RCAlertBox type="success">
        <p className="mb-0">
          {I18n.t('discovery.offers.autoOffers.createForm.sentCount')}
          <span className="weight-bold">{autoOfferType.count.value}</span>
        </p>
      </RCAlertBox>
    </div>
  );
}

function listingTaxIncluded(listing: AutoOfferListingFragment.Fragment) {
  return !!listing.taxIncluded && !!listing.taxIncludedHint;
}

function userCurrencyMatchesShopCurrency(listing: AutoOfferListingFragment.Fragment) {
  return listing.price.currency === listing.sellerPrice.currency;
}

function listingStartingPrice(listing: AutoOfferListingFragment.Fragment) {
  if (listingTaxIncluded(listing) && userCurrencyMatchesShopCurrency(listing)) {
    return listing.price;
  }

  return listing.sellerPrice;
}

function PriceHelper({ listing, dispatch }: IPriceHelperProps) {
  // If the seller is browsing in a different currency, and VAT is involved, don't use the price helper buttons
  if (listingTaxIncluded(listing) && !userCurrencyMatchesShopCurrency(listing)) { return null; }

  const startingPrice = listingStartingPrice(listing);

  return (
    PRICE_HELPER_BUTTON_OPTIONS.map(percentDiscount => (
      <button
        key={percentDiscount}
        className="button button--small mr-half"
        type="button"
        onClick={() => dispatch(
          {
            type: 'setOfferPriceFromPriceHelper',
            value: { percentDiscount, amountCents: startingPrice.amountCents, currency: startingPrice.currency },
          },
        )}
      >
        {I18n.t('discovery.offers.autoOffers.createForm.priceHelper', { percentDiscount })}
      </button>
    ))
  );
}

function LoadingState() {
  return (
    <div className="align-center">
      {I18n.t('discovery.offers.autoOffers.createForm.loading')}
    </div>
  );
}

function GeneralInformation() {
  return (
    <div className="offers-form__notices">
      <RCAlertBox type="plain">
        <p className="offers-form__notice offers-form__notice--watch">
          {I18n.t('discovery.offers.autoOffers.createForm.watch')}
        </p>
        <p className="offers-form__notice offers-form__notice--cart">
          {I18n.t('discovery.offers.autoOffers.createForm.abandonCart')}
        </p>
        <SanitizedRender
          className="offers-form__notice offers-form__notice--confirm"
          htmlTag="p"
          html={
            I18n.t(
              'discovery.offers.autoOffers.createForm.initialSendHtml',
              {
                tagOpen: '<span class="style-italic">',
                tagClose: '</span>',
              },
            )
          }
        />
        <p className="offers-form__notice offers-form__notice--min-price">
          {I18n.t('discovery.offers.autoOffers.createForm.minPrice')}
        </p>
      </RCAlertBox>
    </div>
  );
}

function DisclaimersBox() {
  return (
    <div className="offers-form__notices">
      <p className="offers-form__notice offers-form__notice--time">
        {I18n.t('discovery.offers.autoOffers.createForm.shippingInfo')}
      </p>
      <p className="offers-form__notice offers-form__notice--sale">
        {I18n.t('discovery.offers.autoOffers.createForm.saleInfo')}
      </p>
    </div>
  );
}

function validationError(formState: IState, listing: AutoOfferListingFragment.Fragment) {
  if (!listing || !formState.offerPrice) { return null; }
  if (hasDisabledExistingAutoOffer(formState, listing)) { return null; }

  const inputPriceCents = parseAmount(formState.offerPrice, listing.sellerPrice.currency).amountCents;
  const startingPriceCents = listingStartingPrice(listing).amountCents;

  const discountMultiplier = 1 - (MINIMUM_DISCOUNT_PERCENT / 100);

  if (inputPriceCents > discountMultiplier * startingPriceCents) {
    return I18n.t('discovery.offers.autoOffers.createForm.minimumDiscountError');
  }

  if (inputPriceCents < 1) {
    return I18n.t('discovery.offers.autoOffers.createForm.invalidAmountError');
  }

  return null;
}

export function AutoOfferForm({ loading, listing, createAutoOffer, deleteAutoOffer, closeModal }: IProps) {
  const [formState, dispatch] = React.useReducer(formReducer, emptyFormState);

  React.useEffect(() => {
    if (!loading && hasAutoOffer(listing)) {
      dispatch({ type: 'prefillFormState', value: { listing } });
    }
  }, [listing, loading]);

  if (loading) { return LoadingState(); }

  async function handleSubmit(event) {
    event.preventDefault();
    dispatch({ type: 'setSubmitFailedMessage', value: '' });

    try {
      if (hasDisabledExistingAutoOffer(formState, listing)) {
        await deleteAutoOffer({
          variables: {
            input: {
              listingId: listing.id,
            },
          },
        });
      } else {
        await createAutoOffer({
          variables: {
            input: {
              listingId: listing.id,
              message: formState.messageToBuyer,
              price: parseAmount(
                formState.offerPrice,
                listing.sellerPrice.currency,
              ),
            },
          },
        });
      }

      closeModal();

      // Reload the page since the indicator UI in the auto offer button comes in as a Rails prop
      // If we don't reload, the indicator will be out of sync with the current state of the auto offer
      Location.reload();
    } catch (err) {
      dispatch({ type: 'setSubmitFailedMessage', value: I18n.t('discovery.offers.autoOffers.createForm.submitFailedMessage') });
    }
  }

  const autoOfferCurrency = Currency.get(listing.sellerPrice.currency);
  const validationMessage = validationError(formState, listing);
  const submissionFailedMessage = formState.submitFailedMessage;
  const errorDisplay = validationMessage || submissionFailedMessage;

  return (
    <>
      {GeneralInformation()}
      {hasAutoOffer(listing) && <NegotiationCountBox listing={listing} />}
      <form>
        <FormGroupWithCheckbox
          inputName="toggle-auto-offers"
          label={I18n.t('discovery.offers.autoOffers.createForm.enable')}
          updateField={() => dispatch({ type: 'toggleOfferActive' })}
          checked={formState.autoOfferIsActive}
        />
        {formState.autoOfferIsActive && (
          <div className="scaling-mt-2">
            {errorDisplay && (
              <RCAlertBox type="warning" small>
                <p className="mb-0">
                  {errorDisplay}
                </p>
              </RCAlertBox>
            )}

            <FormGroupWithInput
              autoFocus
              required
              numberOnly
              value={formState.offerPrice}
              label={
                listingTaxIncluded(listing)
                  ? I18n.t('discovery.offers.autoOffers.createForm.priceWithTaxHint', { hint: listing.taxIncludedHint })
                  : I18n.t('discovery.offers.autoOffers.createForm.price')
              }
              inputName="offerPrice"
              currencyPrefix={autoOfferCurrency.symbol}
              currencySuffix={autoOfferCurrency.displayCode}
              helpText={PriceHelper({ dispatch, listing })}
              updateField={({ offerPrice }) =>
                dispatch({ type: 'setOfferPrice', value: offerPrice })
              }
            />

            <FormGroupWithInput
              value={formState.messageToBuyer}
              label={I18n.t('discovery.offers.autoOffers.createForm.message')}
              inputName="messageToBuyer"
              inputType="textarea"
              tagOptional
              updateField={({ messageToBuyer }) =>
                dispatch({ type: 'setMessageToBuyer', value: messageToBuyer })
              }
            />
          </div>
        )}
        <div className="offers-form__actions">
          {hasDisabledExistingAutoOffer(formState, listing) && (
            <div className="mb-2">
              <RCAlertBox type="warning" small>
                <h2 className="weight-bold size-120 mb-half icon-l-exclamation-circle">
                  {I18n.t('discovery.offers.autoOffers.createForm.alertTitle')}
                </h2>
                <p className="mb-0">
                  {I18n.t('discovery.offers.autoOffers.createForm.alertCopy')}
                </p>
              </RCAlertBox>
            </div>
          )}
          {formState.autoOfferIsActive && DisclaimersBox()}
          <button
            className="offers-form__actions__save"
            type="submit"
            onClick={handleSubmit}
            disabled={!formState.offerPrice || !!validationMessage}
          >
            {hasDisabledExistingAutoOffer(formState, listing)
              ? I18n.t('discovery.offers.autoOffers.createForm.save')
              : I18n.t('discovery.offers.autoOffers.createForm.submit')}
          </button>
          <button
            className="reverb-modal__actions__back"
            type="button"
            onClick={closeModal}
          >
            {I18n.t('discovery.offers.autoOffers.createForm.cancel')}
          </button>
        </div>
      </form>
    </>
  );
}

export default flowRight([
  createAutoOfferMutation,
  deleteAutoOfferMutation,
])(AutoOfferForm);
